import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useRecoilState } from "recoil";
import FieldInput from "../../components/form/FieldInput";
import TIcon from "../../components/ui/TIcon";
import AuthShell from "../../components/ui/AuthShell";
import * as SBService from "../../helpers/sportbookService";
import { apiErrorMessage } from "../../helpers/apiError";
import { luuPhienDangNhap } from "../../helpers/authSession";
import { useGoogleSignIn } from "../../hooks/googleSignIn";
import { setAnalyticsUser, trackEvent } from "../../helpers/analytics";
import { loaderState } from "../../states/common";

const REMEMBER_KEY = "biadi.rememberLogin";
/* Khoá giữ nguyên tên cũ dù giờ có thể chứa email: đổi tên khoá là mọi người
   đang bật "ghi nhớ" mất giá trị đã lưu mà chẳng được gì. */
const LAST_LOGIN_KEY = "biadi.lastPhone";

/** Người dùng vừa gõ email hay số điện thoại — chỉ để ghi nhận cách đăng nhập. */
const laEmail = (s: string) => s.includes("@");

const LoginPage = () => {
  const navigate = useNavigate();
  const [, setLoader] = useRecoilState(loaderState);
  const [errorMessage, setErrorMessage] = useState("");
  const [remember, setRemember] = useState(
    () => localStorage.getItem(REMEMBER_KEY) === "1"
  );
  const [formData, setFormData] = useState({ phone: "", password: "" });
  const [formError, setFormError] = useState({ phone: "", password: "" });

  useEffect(() => {
    document.title = "Biadi — Đăng nhập";
    if (localStorage.getItem(REMEMBER_KEY) === "1") {
      const saved = localStorage.getItem(LAST_LOGIN_KEY) ?? "";
      if (saved) setFormData((f) => ({ ...f, phone: saved }));
    }
  }, []);

  const changeInput = (name: string, event: any) => {
    setFormData((f) => ({ ...f, [name]: event.target.value }));
    setFormError((e) => ({ ...e, [name]: "" }));
    setErrorMessage("");
  };

  const submitLogin = async (e: any) => {
    e.preventDefault();

    const nextError = { phone: "", password: "" };
    if (!formData.phone) nextError.phone = "Bắt buộc";
    if (!formData.password) nextError.password = "Bắt buộc";
    setFormError(nextError);
    if (nextError.phone || nextError.password) return;

    try {
      setLoader(true);
      const rsp = await SBService.login(formData);
      if (luuPhienDangNhap(rsp)) {
        if (remember) {
          localStorage.setItem(REMEMBER_KEY, "1");
          localStorage.setItem(LAST_LOGIN_KEY, formData.phone);
        } else {
          localStorage.removeItem(REMEMBER_KEY);
          localStorage.removeItem(LAST_LOGIN_KEY);
        }
        trackEvent("login", { method: laEmail(formData.phone) ? "email" : "phone" });
        setLoader(false);
        navigate("/");
      } else {
        setLoader(false);
        trackEvent("login_failed", { reason: "no_token" });
        setErrorMessage("Đăng nhập không thành công, vui lòng thử lại");
      }
    } catch (err: any) {
      // Đọc `error` rồi dừng là chỗ làm người dùng nhìn thấy mã máy: sai mật khẩu
      // trả về "incorrectPassword", và mọi lỗi 422 khác đều mang mã chung
      // "unprocessableEntity" trong khi lý do thật nằm ở `errors` ngay bên cạnh.
      setErrorMessage(apiErrorMessage(err));
      setLoader(false);
    }
  };

  /* Email chưa có tài khoản thì chuyển sang màn đăng ký, mang theo hồ sơ Google:
     còn thiếu số điện thoại, mà số điện thoại là tên đăng nhập của hệ thống. */
  const { bamGoogle } = useGoogleSignIn({
    setChoDoi: setLoader,
    onLoi: setErrorMessage,
    onCanDangKy: (hoSo) => navigate("/auth/register", { state: { google: hoSo } }),
    onDangNhap: (than) => {
      if (luuPhienDangNhap(than)) {
        trackEvent("login", { method: "google" });
        navigate("/");

        return;
      }
      trackEvent("login_failed", { reason: "no_token" });
      setErrorMessage("Đăng nhập không thành công, vui lòng thử lại");
    },
  });

  return (
    <AuthShell subtitle="Đăng nhập để lưu hạng cơ, lịch sử thi đấu và đăng ký giải đấu.">
      <form className="auth-form" onSubmit={submitLogin}>
        <FieldInput
          /* Một ô cho cả hai: POST /auth/login nhìn thấy '@' thì tra cột email,
             không thì tra cột phone. Người dùng không phải nhớ mình đã đăng ký
             bằng gì, và người đăng nhập bằng Google rồi đặt mật khẩu qua luồng
             quên mật khẩu có thể dùng chính email quen thuộc của họ. */
          label="SỐ ĐIỆN THOẠI HOẶC EMAIL"
          name="phone"
          icon="user"
          placeholder="0901234567 hoặc ban@email.com"
          required
          value={formData.phone}
          error={formError.phone}
          autoComplete="username"
          onChange={changeInput}
        />
        <FieldInput
          label="MẬT KHẨU"
          name="password"
          type="password"
          icon="lock"
          placeholder="••••••••"
          required
          value={formData.password}
          error={formError.password}
          autoComplete="current-password"
          onChange={changeInput}
        />

        <div className="form-meta">
          <button
            type="button"
            className="remember"
            onClick={() => setRemember((r) => !r)}
          >
            <span className={`checkbox${remember ? " on" : ""}`}>
              <TIcon name="check" />
            </span>
            Ghi nhớ đăng nhập
          </button>
          <Link to="/auth/forgot-password">Quên mật khẩu?</Link>
        </div>

        {errorMessage ? (
          <span className="field-error" style={{ whiteSpace: "pre-line" }}>
            <TIcon name="alert-circle" />
            {errorMessage}
          </span>
        ) : null}

        <button type="submit" className="btn btn-primary" style={{ marginTop: 4 }}>
          ĐĂNG NHẬP
        </button>
      </form>

      <div className="auth-divider">
        <span className="rule" />
        <span className="text">hoặc tiếp tục với</span>
        <span className="rule" />
      </div>

      {/* Chỉ Google. Nút Facebook đã bỏ hẳn thay vì để disabled: một nút bấm
          không được chỉ làm người dùng thử rồi tự hỏi mình làm sai gì. */}
      <div className="socials-auth">
        <button type="button" className="social-btn" onClick={() => void bamGoogle()}>
          <TIcon name="brand-google" />
          Google
        </button>
      </div>

      <p className="auth-foot">
        Chưa có tài khoản? <Link to="/auth/register">Đăng ký miễn phí</Link>
      </p>
    </AuthShell>
  );
};

export default LoginPage;
